import React, { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { observer } from 'mobx-react';
import { Router } from 'react-router-dom';
import { createMemoryHistory, createBrowserHistory, History } from 'history';
import { ApolloClient, NormalizedCacheObject, ApolloProvider } from '@apollo/client';
import type { LoginWith2FARecomendation } from '@unitedtraders/login-with-2fa-recomendation';
import type { TokensExtended } from '@unitedtraders/keycloak';
import { Box } from '@utex/ui-kit';

import { I18Service } from './services/i18nservice';
import { RootProps, ViewType } from './types';
import { Navigation } from './components/Navigation';
import { UserBlock } from './components/UserBlock';
import { Authentication } from './components/Authentication';
import { MenuButton } from './components/MenuButton';
import {
  Container,
  Wrapper,
  UserBlockContainer,
  HeaderBlock,
  Logo,
  logoIcon,
  logoText,
} from './styles';
import { IconHeaderLogo, IconHeaderLogoText } from './components/Icons';
import { createApolloClient } from './services/createApolloClient';

const packageInfo = require('../package');
interface FeatureFlags {
  testFlag?: boolean;
}
interface ProfileProps {
  openSideMenu: () => void;
}
interface ViewProps {
  view?: ViewType;
}
interface headerLinks {
  toIdeas: () => void;
  toPortfolio: () => void;
  toIPO: () => void;
  toPreIPO: () => void;
  toAccount: () => void;
  toWithdraw: () => void;
  toDeposit: () => void;
}
interface HeaderProps {
  locale: string;
  isSSR?: boolean;
  isAuthorized?: boolean;
  onLogin: () => void;
  onRegistration: () => void;
  openDepositModal: () => void;
  openWithdrawModal: () => void;
  getTokens: () => Promise<TokensExtended>;
  featureFlags: FeatureFlags;
  navigationActiveItem?: 'ideas' | 'portfolio' | 'howItWorks';
  navigateTo: (path: string) => void;
  customNavCallbacks?: headerLinks;
  loginWith2FARecomendation?: LoginWith2FARecomendation;
}

export type OwnProps = ProfileProps & HeaderProps & ViewProps & RootProps;

let i18n: I18Service;
let apolloClient: ApolloClient<NormalizedCacheObject>;
let history: History;

const isBrowser = () => typeof window !== 'undefined';

export const Header: React.FC<OwnProps> = observer(
  ({
    isAuthorized,
    isSSR,
    openSideMenu,
    view = 'default',
    onRegistration,
    onLogin,
    openDepositModal,
    openWithdrawModal,
    locale,
    getTokens,
    appConfig,
    navigationActiveItem,
    navigateTo,
    loginWith2FARecomendation,
    customNavCallbacks,
  }) => {
    if ((!i18n && !apolloClient) || isSSR) {
      i18n = new I18Service(locale);
      apolloClient = createApolloClient(appConfig.graphqlGateway.url, getTokens);
      history = isBrowser() ? createBrowserHistory() : createMemoryHistory();
    }

    const callbacks = customNavCallbacks || {
      toIdeas: () => navigateTo('/investments'),
      toPortfolio: () => navigateTo('/investments/open-investment'),
      toIPO: () => navigateTo('/how-it-works/ipo'),
      toPreIPO: () => navigateTo('/how-it-works/pre-ipo'),
      toAccount: () => navigateTo('/account/usd'),
    };

    useEffect(() => {
      apolloClient.resetStore();
    }, [isAuthorized]);

    useEffect(() => {
      i18n.update({
        getTokens,
        locale,
        appConfig,
      });
    }, [locale]);

    const [isScrolled, setScroll] = useState(false);

    const scrollHandler = () => {
      const documentScrollTop = isBrowser && document.documentElement.scrollTop;

      if (documentScrollTop >= 60 || document.body.style.position === 'fixed') {
        setScroll(true);
      } else {
        setScroll(false);
      }
    };

    useEffect(() => {
      console.groupCollapsed('Application header started');
      console.info('VERSION', packageInfo.version);
      console.groupEnd();

      isBrowser && window.addEventListener('scroll', scrollHandler);

      return () => {
        isBrowser && window.removeEventListener('scroll', scrollHandler);
      };
    }, []);

    return (
      <Router history={history}>
        <ApolloProvider client={apolloClient}>
          <I18nextProvider i18n={i18n.i18next}>
            <Container view={view} isScrolled={isScrolled} className="compensate-for-scrollbar">
              <Wrapper>
                <HeaderBlock isScrolled={isScrolled} data-testid="header">
                  <Box minWidth={{ tablesktop: 300 }} display="flex">
                    <Logo isScrolled={isScrolled} href={'/'} data-testid="logo-link">
                      <IconHeaderLogo width={34} height={47} css={logoIcon(view, isScrolled)} />
                      <IconHeaderLogoText width={90} height={38} css={logoText(view, isScrolled)} />
                    </Logo>
                  </Box>

                  <Navigation
                    activeItem={navigationActiveItem}
                    view={isScrolled ? 'default' : view}
                    onIdeasClick={callbacks.toIdeas}
                    onPortfolioClick={callbacks.toPortfolio}
                    onIPOClick={callbacks.toIPO}
                    onPreIPOClick={callbacks.toPreIPO}
                  />

                  <Box display={{ mobile: 'none' }}>
                    <UserBlockContainer>
                      {isAuthorized ? (
                        <UserBlock
                          openSideMenu={openSideMenu}
                          view={isScrolled ? 'default' : view}
                          openDeposit={openDepositModal}
                          openWithdraw={openWithdrawModal}
                          onAccountsClick={callbacks.toAccount}
                          is2FADisabled={loginWith2FARecomendation?.isVisible}
                        />
                      ) : (
                        <Box display="flex">
                          {isAuthorized === false && (
                            <Box marginRight={10}>
                              <Authentication
                                onLogin={onLogin}
                                onRegistration={onRegistration}
                                view={isScrolled ? 'default' : view}
                              />
                            </Box>
                          )}
                          <MenuButton
                            openSideMenu={openSideMenu}
                            view={isScrolled ? 'default' : view}
                          />
                        </Box>
                      )}
                    </UserBlockContainer>
                  </Box>

                  <Box display={{ all: 'none', mobile: 'block' }}>
                    <MenuButton openSideMenu={openSideMenu} view={view} />
                  </Box>
                </HeaderBlock>
              </Wrapper>
            </Container>
          </I18nextProvider>
        </ApolloProvider>
      </Router>
    );
  }
);
